import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Gift, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import WizardProgress from '../components/onboarding/WizardProgress';
import StepTypeSelect, { subjectTypeToCard } from '../components/onboarding/StepTypeSelect';
import WhyVoicePro from '../components/onboarding/WhyVoicePro';
import StepDetails from '../components/onboarding/StepDetails';
import StepPhoneVerify from '../components/onboarding/StepPhoneVerify';
import StepActivation from '../components/onboarding/StepActivation';
import StepSuccess from '../components/onboarding/StepSuccess';
import BackToAppBar from '../components/onboarding/BackToAppBar';
import VoiceBioNotice from '../components/onboarding/VoiceBioNotice';
import {
  getOnboardingState,
  saveOnboardingStep,
  submitOnboarding,
} from '../api/creator_onboarding';

/**
 * Creator Onboarding Wizard — 3 steps, no Stripe:
 *   1. Dati Creator   — Privato | Azienda/Professionista + details
 *   2. Verifica telefono (SMS OTP)
 *   3. Termini Creator + attivazione → ACTIVE, can sell immediately.
 *
 * Stripe Express onboarding happens only at payout time
 * (Wallet → "Ricevi i tuoi guadagni").
 */

const FIELDS_TO_PERSIST = [
  'subject_type', 'first_name', 'last_name', 'codice_fiscale',
  'date_of_birth', 'address_line1', 'postal_code', 'city', 'province',
  'country_iso', 'phone', 'business_name', 'vat_number', 'sdi_code',
  'pec_email',
];

const REQUIRED_PRIVATO = [
  'first_name', 'last_name', 'codice_fiscale', 'date_of_birth',
  'address_line1', 'postal_code', 'city', 'province', 'country_iso', 'phone',
];
const REQUIRED_AZIENDA = [
  'first_name', 'last_name', 'business_name', 'vat_number',
  'address_line1', 'postal_code', 'city', 'province', 'country_iso', 'phone',
];

const emptyForm = () =>
  FIELDS_TO_PERSIST.reduce((acc, k) => ({ ...acc, [k]: '' }), {});

const CreatorOnboardingWizard = () => {
  const { t } = useTranslation('onboarding');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm());
  const [prefilledKeys, setPrefilledKeys] = useState([]);
  const [resumed, setResumed] = useState(false);
  const [phoneOk, setPhoneOk] = useState(false);
  const [activated, setActivated] = useState(false);

  const isActiveVip = user?.creator_status === 'active';
  const fullyActivated =
    isActiveVip && !!user?.phone_verified && !!user?.creator_agreement_accepted;

  useEffect(() => {
    if (fullyActivated) {
      navigate('/profile', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const doc = await getOnboardingState();
        if (cancelled) return;
        const fiscal = doc?.fiscal || {};
        const merged = { ...emptyForm(), ...Object.fromEntries(
          FIELDS_TO_PERSIST.map((k) => [k, fiscal[k] || '']),
        ) };
        setForm(merged);
        setPrefilledKeys(['first_name', 'last_name', 'phone']
          .filter((k) => (fiscal[k] || '').length > 0));
        // Resume: fiscal data already submitted (new flow or legacy
        // wizard step >= 4) → jump to phone (2) or activation (3).
        const fiscalDone =
          ['pending_activation', 'active'].includes(doc?.onboarding_status) ||
          (doc?.wizard?.max_step_reached || 1) >= 4;
        if (isActiveVip || fiscalDone) {
          setStep(user?.phone_verified ? 3 : 2);
          setResumed(!isActiveVip);
        }
      } catch (_e) {
        // Fresh session — start from step 1 with a blank form.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setPrefilledKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev));
  }, []);

  const setSubjectType = (v) => setForm((prev) => ({ ...prev, subject_type: v }));

  const goNext = async () => {
    if (step === 1) {
      if (!form.subject_type) {
        toast({ title: t('validation.select_type_first'), variant: 'destructive' });
        return;
      }
      const required = subjectTypeToCard(form.subject_type) === 'individual'
        ? REQUIRED_PRIVATO
        : REQUIRED_AZIENDA;
      if (required.some((k) => !(form[k] || '').trim())) {
        toast({ title: t('validation.fill_required'), variant: 'destructive' });
        return;
      }
      setSaving(true);
      try {
        const patch = {};
        for (const k of FIELDS_TO_PERSIST) {
          if ((form[k] || '') !== '') patch[k] = form[k];
        }
        await saveOnboardingStep({ step: 2, fiscal: patch });
        await submitOnboarding();
        setStep(2);
      } catch (e) {
        const detail = e?.response?.data?.detail || '';
        toast({
          title: 'Errore',
          description: String(detail).includes('|') ? String(detail).split('|')[1] : (String(detail) || 'Riprova'),
          variant: 'destructive',
        });
      } finally {
        setSaving(false);
      }
      return;
    }
    if (step === 2 && phoneOk) setStep(3);
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
    else navigate('/profile');
  };

  const handleActivated = async () => {
    try { await refresh(); } catch (_e) { /* soft */ }
    setActivated(true);
  };

  const progressTitle = useMemo(() => {
    switch (step) {
      case 1: return t('type_step.title');
      case 2: return t('phone_step.title');
      case 3: return t('activation_step.title');
      default: return '';
    }
  }, [step, t]);

  if (loading) {
    return (
      <div className="mx-auto mt-24 flex max-w-md items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('page.loading')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:py-10" data-testid="creator-onboarding-page">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 gold-text" />
        <h1 className="text-2xl font-semibold" data-testid="wizard-page-title">
          {t('page.title')}
        </h1>
      </div>
      <div
        className="gold-gradient mb-5 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-black md:text-sm"
        data-testid="wizard-free-banner"
      >
        <Gift className="h-4 w-4 shrink-0" />
        <span>{t('page.free_banner')}</span>
      </div>

      <BackToAppBar />

      {resumed && !activated && (
        <p className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12px] gold-text" data-testid="wizard-resume-note">
          {t('page.resume_note')}
        </p>
      )}

      {isActiveVip && !activated && (
        <p className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12px] gold-text" data-testid="wizard-completion-note">
          {t('page.completion_note')}
        </p>
      )}

      <WizardProgress current={activated ? 3 : step} total={3} title={progressTitle} />

      {step === 1 && !activated && (
        <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-xl font-semibold text-foreground">{t('intro.title')}</p>
          <p className="mt-2 text-[13px] leading-relaxed text-foreground/90">
            {t('intro.mission')}
          </p>
          <p className="mt-2 text-[12.5px] leading-relaxed text-muted-foreground">
            {t('intro.purpose')}
          </p>
          <p className="mt-2 text-[11.5px] italic text-muted-foreground/80">
            {t('intro.privacy')}
          </p>
        </div>
      )}

      <div className="rounded-2xl border border-white/8 bg-card p-5 md:p-6" data-testid={`wizard-panel-step-${activated ? 'success' : step}`}>
        {activated ? (
          <StepSuccess />
        ) : (
          <>
            {step === 1 && (
              <div data-testid="wizard-step-data">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-foreground" data-testid="type-step-title">
                    {t('type_step.title')}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('type_step.subtitle')}
                  </p>
                </div>
                <StepTypeSelect value={form.subject_type} onChange={setSubjectType} />
                {form.subject_type && (
                  <StepDetails
                    subjectType={form.subject_type}
                    form={form}
                    onFieldChange={handleField}
                    prefilledKeys={prefilledKeys}
                  />
                )}
                {/* Voice Bio is required by the submit: show the guided CTA
                    proactively instead of a raw error toast. */}
                {form.subject_type && !user?.audio_avatar_url && (
                  <VoiceBioNotice className="mt-5" />
                )}
              </div>
            )}
            {step === 2 && (
              <StepPhoneVerify prefillPhone={form.phone} onVerifiedChange={setPhoneOk} />
            )}
            {step === 3 && (
              <StepActivation onActivated={handleActivated} />
            )}
          </>
        )}
      </div>

      {step === 1 && !activated && <WhyVoicePro className="mt-4" />}

      {!activated && step < 3 && (
        <div className="mt-6 flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={saving}
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('progress.back')}
          </Button>
          <Button
            type="button"
            onClick={goNext}
            disabled={saving || (step === 2 && !phoneOk)}
            className="gold-gradient text-black"
            data-testid="wizard-next-btn"
          >
            {saving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('progress.saving')}</>
            ) : (
              <>{step === 1 ? t('progress.next') : t('progress.continue')} <ArrowRight className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreatorOnboardingWizard;
